import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown, Calendar, MapPin } from "lucide-react";
import { useRsvp } from "@/components/wedding/Layout";
import img2026_2 from "@/assets/2026_2.jpg";
import imgIMG_20260406_WA0222 from "@/assets/IMG-20260406-WA0222.jpg";

const events = [
  {
    label: "The Wedding",
    date: "24 June 2026",
    time: "9:30 AM onwards",
    place: "GB Club, Khokurla, Bhandara, Maharashtra",
  },
  {
    label: "The Reception",
    date: "27 June 2026",
    time: "7:30 PM onwards",
    place: "Taj Tristar, Kalasiguda, Secunderabad, Telangana",
  },
];

const featureCards = [
  {
    to: "/story",
    eyebrow: "Chapter One",
    title: "Our Story",
    blurb: "How an engineer and an architect found forever.",
    image: img2026_2,
  },
  {
    to: "/events",
    eyebrow: "The Celebrations",
    title: "Events",
    blurb: "Two cities, four days, one celebration.",
    image:
      "https://images.unsplash.com/photo-1772127822653-4431a9b8c31b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHxpbmRpYW4lMjB3ZWRkaW5nJTIwZmxvcmFsJTIwZGVjb3J8ZW58MHx8fHwxNzgwNjQ5MDIyfDA&ixlib=rb-4.1.0&q=85",
  },
  {
    to: "/gallery",
    eyebrow: "Memories",
    title: "Gallery",
    blurb: "Moments we've gathered along the way.",
    image: imgIMG_20260406_WA0222,
  },
  {
    to: "/venue",
    eyebrow: "Travel & Stay",
    title: "Venue",
    blurb: "Directions, contact and travel notes.",
    image:
      "https://images.pexels.com/photos/12572754/pexels-photo-12572754.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    to: "/guestbook",
    eyebrow: "Well-wishes",
    title: "Guestbook",
    blurb: "Leave a blessing — we'll read every one.",
    image:
      "https://images.unsplash.com/photo-1772127822514-682aeffcc0d3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxpbmRpYW4lMjB3ZWRkaW5nJTIwZmxvcmFsJTIwZGVjb3J8ZW58MHx8fHwxNzgwNjQ5MDIyfDA&ixlib=rb-4.1.0&q=85",
  },
];

export default function HomePage() {
  const { openRsvp } = useRsvp();

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        id="top"
        data-testid="hero-section"
        className="relative w-full min-h-[88vh] sm:min-h-screen overflow-hidden grain"
        style={{ background: "#FBF5EA" }}
      >
        <div
          aria-hidden
          className="absolute -top-32 -right-32 w-[420px] sm:w-[640px] h-[420px] sm:h-[640px] rounded-full opacity-25"
          style={{
            background:
              "radial-gradient(circle at center, #E8902A 0%, transparent 60%)",
          }}
        />
        <div
          aria-hidden
          className="absolute -bottom-40 -left-24 w-[380px] sm:w-[520px] h-[380px] sm:h-[520px] rounded-full opacity-20"
          style={{
            background:
              "radial-gradient(circle at center, #7A1F2B 0%, transparent 65%)",
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-12 min-h-[88vh] sm:min-h-screen flex flex-col">
          <div className="flex-1 flex flex-col justify-center pt-16 pb-12">
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.05 }}
              className="font-serif-wb italic text-lg sm:text-xl mb-3"
              style={{ color: "var(--wb-primary)" }}
              data-testid="hero-invitation-line"
            >
              You are cheerfully invited to our Wedding Reception Ceremony!
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.15 }}
              className="overline"
              data-testid="hero-overline"
            >
              <span className="divider-line mr-4" />
              Together with our families
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.35 }}
              className="font-serif-wb mt-7 sm:mt-10 text-5xl sm:text-7xl lg:text-[9rem] leading-[0.95] tracking-tighter"
              style={{ color: "var(--wb-text)" }}
              data-testid="hero-couple-names"
            >
              Varsha
              <span
                className="italic font-light mx-2 sm:mx-6"
                style={{ color: "var(--wb-primary)" }}
              >
                {" "}&{" "}
              </span>
              Parag
            </motion.h1>

            {/* Dates strip */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.6 }}
              className="mt-10 sm:mt-14 grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-10 max-w-3xl"
              data-testid="hero-dates"
            >
              {events.map((e) => (
                <div
                  key={e.label}
                  className="border-t pt-5"
                  style={{ borderColor: "rgba(122,31,43,0.25)" }}
                >
                  <p className="overline" style={{ color: "var(--wb-primary)" }}>
                    {e.label}
                  </p>
                  <p
                    className="font-serif-wb text-2xl sm:text-3xl mt-2"
                    style={{ color: "var(--wb-text)" }}
                  >
                    {e.date}
                  </p>
                  <div
                    className="mt-2 flex items-center gap-2 text-sm"
                    style={{ color: "var(--wb-text-muted)" }}
                  >
                    <Calendar size={13} />
                    <span>{e.time}</span>
                  </div>
                  <div
                    className="mt-1 flex items-center gap-2 text-sm"
                    style={{ color: "var(--wb-text-muted)" }}
                  >
                    <MapPin size={13} />
                    <span>{e.place}</span>
                  </div>
                </div>
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.9 }}
              className="mt-12 sm:mt-14 flex flex-wrap items-center gap-4 sm:gap-5"
            >
              <button
                onClick={openRsvp}
                data-testid="hero-rsvp-button"
                className="wb-btn"
              >
                RSVP Now
              </button>
              <a
                href="#explore"
                data-testid="hero-scroll-link"
                className="inline-flex items-center gap-2 sm:gap-3 text-[0.72rem] uppercase tracking-[0.22em] border-b border-[var(--wb-text)] pb-1 hover:text-[var(--wb-primary)] hover:border-[var(--wb-primary)] transition-colors"
              >
                <ArrowDown size={14} />
                Explore the site
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FEATURE CARDS — links to inner pages */}
      <section
        id="explore"
        data-testid="home-features"
        className="border-t"
        style={{ background: "#F5EBD3", borderColor: "#EFE6D2" }}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-12 py-20 md:py-28">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-12">
            <div>
              <p className="overline">
                <span className="divider-line mr-4" />
                Begin Anywhere
              </p>
              <h2 className="font-serif-wb mt-5 text-3xl sm:text-4xl lg:text-5xl tracking-tight">
                Wander through{" "}
                <span className="italic" style={{ color: "var(--wb-primary)" }}>
                  our world.
                </span>
              </h2>
            </div>
            <p
              className="text-sm sm:text-base leading-relaxed max-w-sm"
              style={{ color: "var(--wb-text-muted)" }}
            >
              Know our story, explore the events, and find all the details you need to celebrate with us. 
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-7">
            {featureCards.map((c, i) => (
              <motion.div
                key={c.to}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.7, delay: (i % 3) * 0.1 }}
              >
                <Link
                  to={c.to}
                  data-testid={`home-card-${c.title.toLowerCase()}`}
                  className="group block bg-white border border-[#EFE6D2] overflow-hidden h-full"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={c.image}
                      alt={c.title}
                      className="w-full h-full object-cover transition-transform duration-[1100ms] group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-7 sm:p-8">
                    <p className="overline" style={{ color: "var(--wb-primary)" }}>
                      {c.eyebrow}
                    </p>
                    <h3 className="font-serif-wb text-2xl sm:text-3xl mt-3 tracking-tight">
                      {c.title}
                    </h3>
                    <p
                      className="mt-3 text-sm leading-relaxed"
                      style={{ color: "var(--wb-text-muted)" }}
                    >
                      {c.blurb}
                    </p>
                    <span className="mt-6 inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.22em] border-b border-[var(--wb-text)] pb-1 group-hover:text-[var(--wb-primary)] group-hover:border-[var(--wb-primary)] transition-colors">
                      Open <ArrowRight size={13} />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
